import { CinematicModal } from "@/components/ui/CinematicModal";
import {
  cinematicModalInputClass,
  cinematicModalLabelClass,
  cinematicModalPrimaryButtonClass,
  cinematicModalSubtitleClass,
  cinematicModalTextareaClass,
  cinematicModalTitleClass,
} from "@/lib/cinematicModalStyles";
import { MessageSquare, Send } from "lucide-react";
import React, { useEffect, useState } from "react";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, userEmail }) => {
  const [feedback, setFeedback] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && userEmail) {
      setEmail(userEmail);
    }
    if (!isOpen || !userEmail) {
      if (!userEmail) {
        setEmail("");
      }
    }
  }, [isOpen, userEmail]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact-submissions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: "feedback",
          email,
          subject,
          message: feedback,
        }),
      });

      if (!response.ok) {
        throw new Error("Could not save feedback.");
      }
    } catch {
      setIsSubmitting(false);
      setSubmitError("Could not save your feedback. Please try again.");
      return;
    }

    const mailtoLink = `mailto:feedback@chickytutor.com?subject=${encodeURIComponent(
      `Feedback: ${subject}`
    )}&body=${encodeURIComponent(`From: ${email}\n\n${feedback}`)}`;

    if (typeof window !== "undefined") {
      window.location.href = mailtoLink;
    }

    onClose();
    setFeedback("");
    setEmail("");
    setSubject("");
    setIsSubmitting(false);
  };

  return (
    <CinematicModal isOpen={isOpen} onClose={onClose} titleId="feedback-dialog-title">
      <div className="pr-8">
        <h2
          id="feedback-dialog-title"
          className={`${cinematicModalTitleClass} flex items-center gap-2`}
        >
          <MessageSquare className="h-5 w-5 text-orange-300" aria-hidden="true" />
          Send feedback
        </h2>
        <p className={cinematicModalSubtitleClass}>Tell us what is working and what is not.</p>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="feedback-email" className={cinematicModalLabelClass}>
            Your email
          </label>
          <input
            id="feedback-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className={cinematicModalInputClass}
            required
          />
        </div>

        <div>
          <label htmlFor="feedback-subject" className={cinematicModalLabelClass}>
            Subject
          </label>
          <input
            id="feedback-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief summary"
            className={cinematicModalInputClass}
            required
          />
        </div>

        <div>
          <label htmlFor="feedback-message" className={cinematicModalLabelClass}>
            Feedback
          </label>
          <textarea
            id="feedback-message"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Tell us what you think..."
            rows={5}
            className={cinematicModalTextareaClass}
            required
          />
        </div>
        {submitError && <p className="text-sm font-semibold text-red-300">{submitError}</p>}

        <button
          type="submit"
          disabled={isSubmitting}
          className={`${cinematicModalPrimaryButtonClass} w-full`}
        >
          <Send className="h-4 w-4" aria-hidden="true" />
          {isSubmitting ? "Saving..." : "Send feedback"}
        </button>

        <p className="text-center text-xs text-white/45">
          This opens your default email client after saving.
        </p>
      </form>
    </CinematicModal>
  );
};

export default FeedbackModal;
